import { cva, VariantProps } from 'class-variance-authority'
import { ComponentProps, ReactNode } from 'react'
import { cn } from '~utils/cn'

export type ButtonPriority = 'primary' | 'secondary' | 'tertiary' | 'destructive'
export type ButtonSize = 'large' | 'medium' | 'small'

export type ButtonProps = Readonly<
  ComponentProps<'button'> &
    VariantProps<typeof buttonStyle> & {
      children: ReactNode
      priority?: ButtonPriority
      size?: ButtonSize
      loading?: boolean
      rounded?: boolean
    }
>

// eslint-disable-next-line react-refresh/only-export-components
export const buttonPriority = cva('transition-colors', {
  variants: {
    priority: {
      primary: [
        'text-white bg-primary-600 dark:bg-primary-400',
        'active:bg-primary-700',
        'disabled:text-gray-400 disabled:bg-gray-500',
      ],
      secondary: [
        'text-primary-600 bg-transparent border-primary-600 border-[1px]',
        'active:bg-transparent',
        'disabled:border-gray-400 disabled:text-gray-400',
      ],
      tertiary: [
        'text-white bg-secondary-500',
        'active:bg-secondary-700',
        'disabled:bg-secondary-100 disabled:text-white',
      ],
    },
  },
})

const buttonStyle = cva('flex w-fit h-fit flex-row items-center justify-center', {
  variants: {
    priority: {
      primary: buttonPriority({ priority: 'primary' }),
      secondary: buttonPriority({ priority: 'secondary' }),
      tertiary: buttonPriority({ priority: 'tertiary' }),
      destructive: ['text-white bg-red-500 fill-white', 'active:bg-red-600', 'disabled:bg-red-100'],
    },
    size: {
      large: ['px-5 py-[14px]', 'text-body-1'],
      medium: ['px-4 py-3', 'text-body-2'],
      small: ['px-4 py-2', 'text-body-2'],
    },
    rounded: {
      true: 'rounded-full',
      false: 'rounded-2xl',
    },
  },
  defaultVariants: {
    priority: 'primary',
    size: 'large',
    rounded: true,
  },
})

/**
 * @version v1.1.0
 * @author Danny
 * @description tertiary 버튼 텍스트 색상 변경
 */
const Button = ({ children, priority, size, rounded, ...props }: ButtonProps) => {
  const { className, type = 'button' } = props

  return (
    <button
      {...props}
      type={type}
      className={cn(buttonStyle({ priority, size, rounded }), className)}
    >
      {children}
    </button>
  )
}

export default Button
